import React, { useState } from 'react';
import { AnimatedCheckbox } from './common';
import generatePDF from '../pdf/printAsTable';

interface AnimatedCheckboxProps {
  isChecked: boolean;
  onChange: (e:any) => void;
}


interface TableColumn {
  header: string;
  key: string;
  render?: (item: any, index:number) => React.ReactNode; 
}

interface ActionButton {
  label: string;
  onClick: (selectedItems: Set<any>) => void;
}

interface GenericSelectableTableProps {
  columns: TableColumn[];
  data: any[];
  bullkActionButtons?: ActionButton[];
  printableColumn?: any[];
}

const getValueByPath = (obj: any, path: string): any => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const GenericSelectableTable: React.FC<GenericSelectableTableProps> = ({ columns, data, bullkActionButtons, printableColumn }) => {
  const [selectedItems, setSelectedItems] = useState<Set<any>>(new Set());

  const toggleSelection = (itemId: any) => {
    setSelectedItems((prevSelected) => {
      const updatedSelection = new Set(prevSelected);
      if (updatedSelection.has(itemId)) {
        updatedSelection.delete(itemId);
      } else {
        updatedSelection.add(itemId);
      }
      return updatedSelection;
    });
  };

  const handleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(new Set(data.map((item) => item.id || item?.node?.dbId || item.dbId)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const defaultBulkActions: ActionButton[] = [
    
  ]

  if (printableColumn) {
    defaultBulkActions.push({
      label: 'Print Selected',
      onClick: (selectedItems) => {
        generatePDF(Array.from(selectedItems), data, printableColumn);
      },
    });
  }

  const allBulkActions = bullkActionButtons ? [...defaultBulkActions, ...bullkActionButtons] : defaultBulkActions;

  return (
    <div className='mt-2'>
     
      {selectedItems.size > 0 && (
        <div className="mb-4 flex gap-2 flex-wrap ml-2">

          {allBulkActions.map((button) => (
            <button
              key={button.label}
              onClick={() => button.onClick(selectedItems)}
              className="px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
            >
              {button.label}
            </button>
          ))}
          <p className="ml-2 px-2 py-1 text-sm font-medium text-white bg-[#0CAF60] rounded-full">
            Total of {selectedItems.size} items selected
          </p>

        </div>
      )}

      <table className="max-w-full min-w-0 w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th>
              <div className='px-4 py-2 flex items-center'>
                <AnimatedCheckbox
                  onChange={(e:any) => handleSelectAll(e.target.checked)}
                  isChecked={selectedItems.size === data.length && data.length > 0}
                />
              </div>
            </th>
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-600 text-nowrap">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">

        {data && data.length > 0 ? (
            data.map((item, index) => (
              <tr key={index} className="hover:bg-gray-100">
                <td>
                  <div className='px-4 py-2 flex items-center'>
                    <AnimatedCheckbox
                      isChecked={selectedItems.has(item.id || item?.node?.dbId || item.dbId)}
                      onChange={(e:any) => toggleSelection(item.id || item?.node?.dbId || item.dbId)}
                    />
                  </div>
                </td>
                {columns.map((col, i) => (
                  <td key={i} className="px-4 py-2 text-gray-800">
                    {col.render ? col.render(item, index) : getValueByPath(item, col.key) ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length + 1} className="px-6 py-12 text-center">
              <div className="flex flex-col items-center justify-center text-gray-500">
                <svg className="w-12 h-12 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-lg font-medium">No Data Found</p>
                <p className="text-sm">No data available at the moment</p>
              </div>
              </td>
            </tr>
          )}

        </tbody>
      </table>
    </div>
  );
};

export default GenericSelectableTable;
